import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Coins, Scale, ScrollText } from 'lucide-react';
import { useZakatGlobal } from '../../context/ZakatGlobalContext';

/**
 * ProductCardsV2 — three signature tool cards.
 *
 * Restored differentiated colorway per user feedback (Feb 2026): the
 * shared emerald treatment made the row feel flat. Each product now owns
 * a distinct surface drawn from Islamic-manuscript palettes:
 *   • Zakat  — emerald with gold-star watermark
 *   • Faraid — lapis / manuscript-ink blue
 *   • Will   — cream-to-gold parchment
 *
 * All three cards share the same layout, typography, corner-radius and
 * height — only the surface / accent colours change. No number straps,
 * no time chips, no feature-list micro-text (those live inside the tool).
 */
const CardGeometry = ({ tone = 'gold', opacity = 0.09 }) => {
  const stroke = tone === 'gold' ? '#B59A6D' : '#0D1520';
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 240 240"
      className="absolute -right-6 -top-6 w-36 h-36 pointer-events-none"
      style={{ opacity }}
    >
      <g fill="none" stroke={stroke} strokeWidth="0.9">
        <polygon points="120,12 156,60 216,60 180,108 216,156 156,156 120,204 84,156 24,156 60,108 24,60 84,60" />
        <polygon points="120,52 138,80 172,80 156,108 172,136 138,136 120,164 102,136 68,136 84,108 68,80 102,80" />
        <circle cx="120" cy="108" r="30" />
      </g>
    </svg>
  );
};

const CARDS = [
  {
    id: 'zakat',
    Icon: Coins,
    tkey: 'zakat',
    to: '/zakat/calculator',
    testid: 'card-zakat',
    surface: 'bg-[#EDF5F1] border-[#CBD2D9] hover:border-gold-500/60',
    surfaceStyle: null,
    iconBg: 'bg-gold-500/15 text-gold-300 ring-1 ring-gold-500/25',
    title: 'text-[#0D1520]',
    subtitle: 'text-[#4A5568]',
    arrow: 'text-gold-300',
    geoTone: 'gold',
  },
  {
    id: 'faraid',
    Icon: Scale,
    tkey: 'faraid',
    to: '/inheritance',
    testid: 'card-faraid',
    surface: 'text-[#0D1520] border-lapis-700 hover:border-gold-500/60',
    surfaceStyle: { background: '#0D4733' },
    iconBg: 'bg-gold-500/15 text-gold-300 ring-1 ring-gold-500/25',
    title: 'text-[#0D1520]',
    subtitle: 'text-[#4A5568]',
    arrow: 'text-gold-300',
    geoTone: 'gold',
  },
  {
    id: 'will',
    Icon: ScrollText,
    tkey: 'will',
    to: '/will',
    testid: 'card-will',
    surface: 'text-[#0D1520] border-gold-500/40 hover:border-gold-500',
    surfaceStyle: { background: 'linear-gradient(155deg, #FAF7F2 0%, #F1EDE4 55%, #E8D6AF 100%)' },
    iconBg: 'bg-[#EDF5F1] text-gold-400 ring-1 ring-[#0D4733]',
    title: 'text-[#0D1520]',
    subtitle: 'text-stone-700',
    arrow: 'text-[#0D1520]',
    geoTone: 'green',
  },
];

export const ProductCardsV2 = () => {
  const navigate = useNavigate();
  const { t } = useZakatGlobal();
  return (
    <section
      id="product-cards-v2"
      className="gd-anchor relative bg-[#EDF5F1] py-6 md:py-10"
      data-testid="product-cards-v2"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          {CARDS.map((c) => {
            const Icon = c.Icon;
            const title = t(`v2.cards.${c.tkey}.title`);
            const subtitle = t(`v2.cards.${c.tkey}.subtitle`);
            return (
              <button
                key={c.id}
                onClick={() => navigate(c.to)}
                data-testid={c.testid}
                type="button"
                style={c.surfaceStyle || undefined}
                className={`group relative text-left rounded-2xl border transition-all duration-300 hover:-translate-y-0.5 shadow-sm hover:shadow-lg p-5 md:p-6 overflow-hidden min-h-[44px] ${c.surface}`}
              >
                <CardGeometry tone={c.geoTone} opacity={c.geoTone === 'gold' ? 0.09 : 0.06} />
                <div className="relative flex items-start gap-3">
                  <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${c.iconBg}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className={`font-heading text-[18px] leading-tight ${c.title}`}>
                      {title}
                    </h3>
                    <p className={`mt-1.5 text-[14px] leading-snug ${c.subtitle}`}>
                      {subtitle}
                    </p>
                  </div>
                  <ArrowRight className={`w-4 h-4 mt-1.5 transition-transform group-hover:translate-x-0.5 ${c.arrow}`} />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProductCardsV2;
